import { roundTo } from "round-to";
import { filterRecordsByDate, months } from "../../../utils/helper";
const { parseISO } = require('date-fns');

function groupByStore(data) {
    const grouped = {};
    data?.forEach(item => {
        const store = `${item.idUnidade} - ${item.unidadeName}`;
        if (!grouped[store]) grouped[store] = { store, records: [] };
        grouped[store].records.push(item);
    });
    return Object.values(grouped);
}

function groupByMonthWithDays(records) {
    const byMonth = {};
    records.forEach(item => {
        const m = item.mes;
        const y = item.ano;
        if (!byMonth[m]) {
            byMonth[m] = { month: m, monthName: months[m - 1], year: y, records: [] };
        }
        byMonth[m].records.push(item);
    });

    return Object.values(byMonth)
        .sort((a, b) => a.month - b.month)
        .map(({ month, monthName, year, records }) => {
            // agrupa por dia
            const byDay = {};
            records.forEach(r => {
                const d = r.dia;
                if (!byDay[d]) byDay[d] = [];
                byDay[d].push(r);
            });
            // quantos dias tem o mês
            const daysInMonth = new Date(year, month, 0).getDate();
            // monta array de dias, preenchendo zeros
            const days = [];
            for (let d = 1; d <= daysInMonth; d++) {
                const recs = byDay[d] || [];
                const valorTotal = recs.reduce((sum, r) => sum + parseFloat(r.valorTotal || 0), 0);
                const cmv = recs.reduce((sum, r) => sum + parseFloat(r.cmv || 0), 0);
                const pctCmv = valorTotal > 0 ? (cmv / valorTotal) * 100 : 0;
                days.push({
                    day: d,
                    valorTotal: valorTotal.toFixed(2),
                    cmv: cmv.toFixed(2),
                    percentualCmv: pctCmv.toFixed(2)
                });
            }
            return { year, month, monthName, days };
        });
}

export const PerformanceByStore = (
    kpiPerformance,
    sortBy = "ALPHABETICAL"
) => {
    const result = {
        caption: 'Performance por unidade',
        data: [],      // [{ store, totalCurrent, averageOthers, months: [...] }]
        summary: [],
        index: "store",
        categories: [],// [{ month, monthName }]
        colors: ["indigo", "cyan"],
        yAxisWidth: 90,
    };

    const list = kpiPerformance?.listAll;
    const currentMonth = kpiPerformance?.currentMonth;
    const currentDay = kpiPerformance?.currentDay;
    if (!Array.isArray(list) || !list.length || typeof currentMonth !== 'number') {
        return result;
    }

    // 1) agrupa por unidade
    const byStore = groupByStore(list);

    // 2) mapeia cada unidade e calcula totais e médias
    const mapped = byStore.map(({ store, records }) => {
        const monthsArr = groupByMonthWithDays(records);

        // dados do mês atual
        const current = monthsArr.find(m => m.month === currentMonth) || { days: [] };
        const totalCurrent = current.days.reduce((sum, d) => sum + parseFloat(d.valorTotal), 0);
        const cmvCurrent = current.days.reduce((sum, d) => sum + parseFloat(d.cmv), 0);
        const pctCurrent = totalCurrent > 0 ? (cmvCurrent / totalCurrent) * 100 : 0;

        // média dos outros meses
        const others = monthsArr.filter(m => m.month !== currentMonth);
        const nOthers = others.length || 1;
        const sums = others.reduce((acc, m) => {
            const tot = m.days.reduce((s, d) => s + parseFloat(d.valorTotal), 0);
            const cm = m.days.reduce((s, d) => s + parseFloat(d.cmv), 0);
            const pct = tot > 0 ? (cm / tot) * 100 : 0;
            return {
                valorTotal: acc.valorTotal + tot,
                cmv: acc.cmv + cm,
                percentualCmv: acc.percentualCmv + pct,
            };
        }, { valorTotal: 0, cmv: 0, percentualCmv: 0 });

        const averageOthers = {
            valorTotal: (sums.valorTotal / nOthers).toFixed(2),
            cmv: (sums.cmv / nOthers).toFixed(2),
            percentualCmv: (sums.percentualCmv / nOthers).toFixed(2),
        };

        // ordena considerando ano e mês
        const sortedMonths = [...monthsArr].sort(
            (a, b) => (a.year - b.year) || (a.month - b.month)
        );


        return {
            store,
            totalCurrent: {
                valorTotal: totalCurrent.toFixed(2),
                cmv: cmvCurrent.toFixed(2),
                percentualCmv: pctCurrent.toFixed(2),
            },
            averageOthers,
            months: sortedMonths,
            currentMonth,
            currentDay
        };
    });

    // 3) ordenação conforme parâmetro
    if (sortBy === "TOTAL") {
        // decrescente por totalCurrent.valorTotal
        result.data = mapped.sort((a, b) =>
            parseFloat(b.totalCurrent.valorTotal) - parseFloat(a.totalCurrent.valorTotal)
        );
    } else {
        // padrão: alfabética por store
        result.data = mapped.sort((a, b) =>
            a.store.localeCompare(b.store, 'pt', { sensitivity: 'base' })
        );
    }

    // 4) categorias (meses) a partir de todas as unidades
    // console.log('result.data', result.data)
    const monthSet = new Set();

    result.data.forEach(({ months }) =>
        months.forEach(({ month }) => monthSet.add(month))
    );

    result.categories = Array.from(monthSet)
        .sort((a, b) => a - b)
        .map(m => ({ month: m, monthName: months[m - 1] }));
    // console.log('result', result)

    return result;
};



export const ComputeCurrentCmvKpi = (kpiPerformance) => {
    const listAll = Array.isArray(kpiPerformance?.listAll)
        ? kpiPerformance.listAll
        : [];

    const currentMonth = kpiPerformance?.currentMonth;
    if (!listAll.length || typeof currentMonth !== 'number') {
        return {
            caption: 'CMV Total',
            metric: '0.00%',
            valueProgress: 'R$ 0,00',
            badgeDelta: 0,
            color: 'yellow',
        };
    }

    // 1) soma de CMV e valorTotal por mês
    const sumsByMonth = listAll.reduce((acc, r) => {
        const m = r.mes;
        const cmv = parseFloat(r.cmv) || 0;
        const valor = parseFloat(r.valorTotal) || 0;
        if (!acc[m]) acc[m] = { cmv: 0, valor: 0 };
        acc[m].cmv += cmv;
        acc[m].valor += valor;
        return acc;
    }, {});

    // 2) pega dados do mês atual
    const { cmv = 0, valor = 0 } = sumsByMonth[currentMonth] || {};

    // 3) calcula percentual de CMV no mês atual
    const currentPct = valor > 0
        ? (cmv / valor) * 100
        : 0;

    // 4) calcula média dos percentuais dos outros meses
    const otherPcts = Object.entries(sumsByMonth)
        .filter(([m]) => Number(m) !== currentMonth)
        .map(([, { cmv, valor }]) =>
            valor > 0 ? (cmv / valor) * 100 : 0
        );

    const avgPct = otherPcts.length > 0
        ? otherPcts.reduce((a, b) => a + b, 0) / otherPcts.length
        : 0;

    // 5) badgeDelta: variação do percentual atual vs média histórica
    const badgeDelta = avgPct > 0
        ? ((currentPct - avgPct) / avgPct) * 100
        : 0;

    // 6) progress: percentual atual sobre média histórica
    const progress = avgPct > 0
        ? (currentPct / avgPct) * 100
        : 0;


    return {
        caption: 'CMV Total',
        descriptionComparative: 'Outros meses',
        metric: `${currentPct.toFixed(2)}%`,
        target: `${avgPct.toFixed(2)}%`,
        badgeDelta: parseFloat(badgeDelta.toFixed(2)),
        progress: parseFloat(progress.toFixed(2)),
    };
};


export const CumulativeSalesPerStore = (sales) => {
    let result = {
        caption: 'Vendas por unidade',
        data: [],
        summary: [],
        index: "store",
        categories: [],
        colors: ["indigo", "cyan"],
        yAxisWidth: 90,
    };

    // Verifica se há vendas no mês atual
    const existsCurrentMonthSales = sales?.currentMonthSales?.length;
    if (!existsCurrentMonthSales) return result; // Retorna um resultado vazio se não houver vendas

    // Identifica o mês atual e o mês anterior
    const actualMonth = sales.currentMonth - 1;
    const lastMonth = actualMonth === 0 ? 11 : actualMonth - 1;
    result.categories.push(months[lastMonth], months[actualMonth]);

    // Função para extrair as lojas a partir dos dados
    const extractStore = (data) => {
        const stores = data.map(item => ({ store: item.idUnidade, description: item.unidadeName }));
        return [...new Set(stores.map(store => JSON.stringify(store)))].map(store => JSON.parse(store)); // Remove duplicados
    };
    // Função para agrupar os dados por data e loja
    const groupByDateAndStore = (data, totalDaysInMonth = 31) => {
        const groupedData = data.reduce((acc, { dia, idUnidade, valorTotal }) => {
            if (!acc[dia]) acc[dia] = { dia }; // Inicializa a chave do dia
            acc[dia][idUnidade] = (acc[dia][idUnidade] || 0) + Number(valorTotal); // Acumula as vendas por loja e dia
            return acc;
        }, {});

        // Preenche dias faltantes com valor 0 para todas as lojas
        for (let dia = 1; dia <= totalDaysInMonth; dia++) {
            if (!groupedData[dia]) groupedData[dia] = { dia };
            const storeList = extractStore(sales.currentMonthSales);
            storeList.forEach(store => {
                if (!groupedData[dia][store.store]) {
                    groupedData[dia][store.store] = 0; // Garante que todas as lojas têm valores, mesmo que seja 0
                }
            });
        }

        return Object.values(groupedData);
    };

    const groupByDateAndStoreCurrentMonth = groupByDateAndStore(sales.currentMonthSales);
    let groupByDateAndStoreLastMonth;

    // Filtra os dados do mês anterior com base na comparação de data
    if ((!sales.compareWith || sales.compareWith === 'CURRENT_DATE') && sales.isCurrentMonth) {
        groupByDateAndStoreLastMonth = groupByDateAndStore(filterRecordsByDate(sales.lastMonthSales, sales.currentDay));
    } else {
        groupByDateAndStoreLastMonth = groupByDateAndStore(filterRecordsByDate(sales.lastMonthSales));
    }
    const storeList = extractStore(sales.currentMonthSales);



    // Função para buscar uma loja específica pelo ID
    const filterByStore = (data, storeId) => data?.find(item => item?.store === storeId);

    // Função para consolidar as vendas por loja
    const consolidateByStore = (data) => {
        const consolidated = data.reduce((acc, { dia, ...stores }) => {
            Object.keys(stores).forEach((storeId) => {
                const valor = stores[storeId];
                if (!acc[storeId]) acc[storeId] = { store: storeId, totalValue: 0 };
                acc[storeId].totalValue += parseFloat(valor); // Acumula o valor total das vendas
            });
            return acc;
        }, {});
        return Object.values(consolidated); // Retorna as lojas consolidadas
    };

    // Consolida os dados do mês atual e do mês anterior
    const consolidatedByStoreCurrentMonth = consolidateByStore(groupByDateAndStoreCurrentMonth);
    const consolidatedByStoreLastMonth = consolidateByStore(groupByDateAndStoreLastMonth);

    const sortedConsolidatedCurrentMonth = consolidatedByStoreCurrentMonth.sort((a, b) => b.totalValue - a.totalValue); // Ordenando em ordem decrescente




    const consolidatedWithChange = sortedConsolidatedCurrentMonth.map(item => {
        const totalLast = filterByStore(consolidatedByStoreLastMonth, item.store);
        const change = totalLast && totalLast.totalValue !== 0
            ? ((item.totalValue - totalLast.totalValue) / totalLast.totalValue) * 100
            : 0; // Calcula a variação percentual
        const changeType = totalLast && totalLast.totalValue < item.totalValue ? 'positive' : 'negative'; // Define o tipo de mudança (positivo ou negativo)

        return {
            ticker: item.store,
            description: filterByStore(storeList, item.store)?.description || '', // Descrição da loja
            value: item.totalValue,
            change: roundTo(change, 2) + '%', // Arredonda a mudança para 2 casas decimais
            changeType: changeType, // Tipo de mudança
        };
    });

    result.data = groupByDateAndStoreCurrentMonth; // Dados agrupados para o mês atual
    result.summary = consolidatedWithChange; // Resumo com as mudanças de vendas

    return result; // Retorna o resultado final
};

export const ProductionPerformanceSummary = (dadosPorLoja) => {

    if (!Array.isArray(dadosPorLoja) || dadosPorLoja.length === 0) {
        return {
            caption: 'T.M.P Delivery',
            metric: '0 min',
            target: '0 min',
            badgeDelta: 0,
            descriptionComparative: 'Mês Anterior'
        };
    }

    let somaAtual = 0;
    let somaAnterior = 0;
    let total = 0;

    dadosPorLoja.forEach(loja => {
        const atual = loja?.currentMonth?.tempoProducao?.media;
        const anterior = loja?.lastMonth?.tempoProducao?.media;

        if (!isNaN(atual)) {
            somaAtual += atual;
            somaAnterior += isNaN(anterior) ? 0 : anterior;
            total++;
        }
    });

    const tempoProducaoAtual = total > 0 ? somaAtual / total : 0;
    const tempoProducaoAnterior = total > 0 ? somaAnterior / total : 0;
    const variacao = calcularVariacaoPercentual(tempoProducaoAnterior, tempoProducaoAtual);


    // let deltaType;
    // if (typeof variacao === 'number') {
    //     deltaType = variacao > 0 ? 'decrease' : variacao < 0 ? 'increase' : 'unchanged';
    // } else {
    //     deltaType = undefined; // não renderiza badge se não houver delta
    // }

    const badgeDelta = variacao;

    const metric = `${Math.abs(tempoProducaoAtual).toFixed(0)} min`;
    const target = `${Math.abs(tempoProducaoAnterior).toFixed(0)} min`;

    return {
        descriptionComparative: 'Mês Anterior',
        caption: 'T.M.P Delivery',
        metric,
        target,
        badgeDelta
    };
};

function calcularVariacaoPercentual(valorAnterior, valorAtual) {
    if (valorAnterior === 0) {
        return valorAtual === 0 ? 0 : 100;
    }
    return Math.round(((valorAtual - valorAnterior) / valorAnterior) * 1000) / 10; // 1 casa decimal
}


export const CalculateTimesByStore = (kpiOrderIntegration) => {
    const { currentMonthOrderIntegration, lastMonthOrderIntegration } = kpiOrderIntegration || {};

    const existsCurrentMonth = currentMonthOrderIntegration && currentMonthOrderIntegration.length;
    const existsLastMonth = lastMonthOrderIntegration && lastMonthOrderIntegration.length;

    if (!existsCurrentMonth) {
        return {};
    }

    const unidadeNames = {};
    currentMonthOrderIntegration.forEach(p => {
        unidadeNames[p.idUnidade] = p.unidadeName;
    });
    if (existsLastMonth) {
        lastMonthOrderIntegration.forEach(p => {
            unidadeNames[p.idUnidade] = p.unidadeName;
        });
    }

    // Função auxiliar para processar os dados de um mês
    const processMonth = (orderList) => {
        const resultado = {};

        orderList
            .filter(p => p.tipoPedido === 'DELIVERY')
            .forEach(p => {
                const unidade = p.idUnidade;
                const dataInicioProducao = p.dataPedido;
                const dataFimProducao = p.dataAguardandoRetirada || p.dataEnvio;

                const tempos = {
                    tempoEntrePedidoEConhecimento: getDiff(p.dataPedido, p.dataConhecimento),
                    tempoEntreConhecimentoEConfirmacao: getDiff(p.dataConhecimento, p.dataConfirmacao),
                    tempoProducao: getDiff(dataInicioProducao, dataFimProducao)
                };

                if (!resultado[unidade]) {
                    resultado[unidade] = {
                        contagem: 0,
                        etapas: {
                            tempoEntrePedidoEConhecimento: [],
                            tempoEntreConhecimentoEConfirmacao: [],
                            tempoProducao: []
                        }
                    };
                }

                resultado[unidade].contagem += 1;

                for (const etapa in tempos) {
                    if (!isNaN(tempos[etapa])) {
                        resultado[unidade].etapas[etapa].push(tempos[etapa]);
                    }
                }
            });

        // Estatísticas por unidade
        const saida = {};
        for (const [idUnidade, dados] of Object.entries(resultado)) {
            const etapas = {};
            for (const etapa in dados.etapas) {
                const valores = dados.etapas[etapa];
                etapas[etapa] = {
                    media: media(valores),
                    minimo: Math.min(...valores),
                    maximo: Math.max(...valores)
                };
            }
            saida[idUnidade] = etapas;
        }

        return saida;
    };

    const dadosCurrentMonth = processMonth(currentMonthOrderIntegration);
    const dadosLastMonth = existsLastMonth ? processMonth(lastMonthOrderIntegration) : {};

    const todasUnidades = new Set([
        ...Object.keys(dadosCurrentMonth),
        ...Object.keys(dadosLastMonth)
    ]);

    const tempoZerado = {
        media: 0,
        minimo: 0,
        maximo: 0
    };

    const etapasZeradas = {
        tempoEntrePedidoEConhecimento: tempoZerado,
        tempoEntreConhecimentoEConfirmacao: tempoZerado,
        tempoProducao: tempoZerado
    };

    const resultadoFinal = Array.from(todasUnidades).map(idUnidade => ({
        idUnidade,
        store: `${idUnidade} - ${unidadeNames[idUnidade] || ''}`,
        currentMonth: dadosCurrentMonth[idUnidade] || etapasZeradas,
        lastMonth: existsLastMonth
            ? (dadosLastMonth[idUnidade] || etapasZeradas)
            : etapasZeradas
    }));

    return resultadoFinal;
};


// Helpers
function getDiff(start, end) {
    try {
        if (!start || !end) return NaN;
        return (parseISO(end) - parseISO(start)) / 60000;
    } catch {
        return NaN;
    }
}

function media(valores) {
    const total = valores.reduce((acc, v) => acc + v, 0);
    return Math.round((total / valores.length) * 100) / 100;
}
